/**
 * EditProjectDialog - Dialog for editing project Deal Source, Sector, and Transaction Type
 *
 * Allows inline editing of key project metadata from the dashboard
 */

import { useState, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import type { Project, DealStructureType } from '../../hooks/useProjects';
import { recordProjectEditCompleted } from '../../lib/analytics/productEvents';

interface EditProjectDialogProps {
  project: Project | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (projectId: string, updates: {
    industry?: string;
    dealStructureType?: DealStructureType;
    metadata?: { dealSource?: string };
  }) => Promise<void>;
}

// Deal Structure Type options
const DEAL_STRUCTURE_OPTIONS: { value: DealStructureType; label: string }[] = [
  { value: 'platform', label: 'Platform' },
  { value: 'add_on', label: 'Add-on' },
  { value: 'growth', label: 'Growth' },
  { value: 'buyout', label: 'Buyout' },
  { value: 'carve_out', label: 'Carve-out' },
  { value: 'recap', label: 'Recapitalization' },
  { value: 'other', label: 'Other' },
];

export function EditProjectDialog({
  project,
  open,
  onOpenChange,
  onSave,
}: EditProjectDialogProps) {
  const [dealSource, setDealSource] = useState('');
  const [sector, setSector] = useState('');
  const [transactionType, setTransactionType] = useState<DealStructureType | ''>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Initialize form when project changes
  useEffect(() => {
    if (project) {
      setDealSource(project.metadata?.dealSource || '');
      setSector(project.industry || '');
      setTransactionType(project.dealStructureType || '');
      setSaveError(null);
    }
  }, [project]);

  useEffect(() => {
    if (open) {
      setSaveError(null);
    }
  }, [open]);

  const handleSave = async () => {
    if (!project) return;

    setSaving(true);
    setSaveError(null);
    let updatedFields: string[] = [];
    try {
      const updates: {
        industry?: string;
        dealStructureType?: DealStructureType;
        metadata?: { dealSource?: string };
      } = {};

      // Only include changed fields
      if (sector !== project.industry) {
        updates.industry = sector;
      }

      if (transactionType && transactionType !== project.dealStructureType) {
        updates.dealStructureType = transactionType;
      }

      if (dealSource !== (project.metadata?.dealSource || '')) {
        const metadata = { ...project.metadata };
        if (dealSource) {
          metadata.dealSource = dealSource;
        } else {
          delete metadata.dealSource;
        }
        updates.metadata = metadata;
      }

      if (Object.keys(updates).length > 0) {
        updatedFields = Object.keys(updates).map((field) => field === 'metadata' ? 'deal_source' : field);
        await onSave(project.id, updates);
        recordProjectEditCompleted({
          projectId: project.id,
          fieldsUpdated: updatedFields,
          outcome: 'success',
        });
      }

      onOpenChange(false);
    } catch (error) {
      console.error('Error saving project:', error);
      recordProjectEditCompleted({
        projectId: project.id,
        fieldsUpdated: updatedFields,
        outcome: 'failed',
      });
      setSaveError(error instanceof Error ? error.message : 'Unable to save project. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!project) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-primary" />
            Edit Project
          </DialogTitle>
          <DialogDescription>
            Update deal information for <span className="font-medium">{project.name}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {saveError && (
            <p role="alert" className="text-sm text-destructive">
              {saveError}
            </p>
          )}
          {/* Deal Source */}
          <div className="grid gap-2">
            <Label htmlFor="dealSource">Deal Source</Label>
            <Input
              id="dealSource"
              value={dealSource}
              onChange={(e) => setDealSource(e.target.value)}
              placeholder="e.g., Jefferies, proprietary, management referral"
            />
            <p className="text-xs text-muted-foreground">
              The intermediary or source that brought this deal
            </p>
          </div>

          {/* Sector */}
          <div className="grid gap-2">
            <Label htmlFor="sector">Sector</Label>
            <Input
              id="sector"
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              placeholder="e.g., Healthcare Services, Business Services"
            />
            <p className="text-xs text-muted-foreground">
              The industry or sector of the target company
            </p>
          </div>

          {/* Transaction Type */}
          <div className="grid gap-2">
            <Label htmlFor="transactionType">Transaction Type</Label>
            <Select
              value={transactionType}
              onValueChange={(value) => setTransactionType(value as DealStructureType)}
            >
              <SelectTrigger id="transactionType">
                <SelectValue placeholder="Select transaction type" />
              </SelectTrigger>
              <SelectContent>
                {DEAL_STRUCTURE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              The type of transaction structure
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
